<template>
  <v-stepper-window-item ref="stepRoot" :value="stepValue">
     <!-- Import Tasks Dialog -->
     <v-dialog
      v-model="importDialog"
      max-width="500"
      scrollable
      :persistent="saving"
      content-class="save-tasks-dialog"
    >
      <v-card>
        <v-card-title class="text-h6 px-4 pt-4">
          Save Tasks
        </v-card-title>

        <v-card-text>
          <div class="d-flex justify-space-around mb-4">
            <v-chip color="green" label>
              New: {{ newCount }}
            </v-chip>
            <v-chip color="orange" label>
              Updated: {{ updatedCount }}
            </v-chip>
            <v-chip color="red" label>
              Deleted: {{ deletedCount }}
            </v-chip>
          </div>

          <!-- New-schedule verification: the template choice and per-time-range
               calendars are part of what the user approves. Changing a calendar
               restamps every row on that range and recomputes its end dates
               against the new working-day calendar. -->
          <v-alert
            v-if="newScheduleProposal"
            type="info"
            variant="tonal"
            density="compact"
            class="mb-4"
          >
            <div :class="{ 'mb-2': trackingRangeTemplates.length > 0 }">
              New schedule <strong>{{ newScheduleProposal.title }}</strong>
              ({{ newScheduleProposal.code }}) —
              template: <strong>{{ newScheduleTemplateTitle }}</strong>
            </div>
            <template v-if="trackingRangeTemplates.length > 0">
              <div v-if="calendarDropdownItems.length === 1" class="text-caption">
                Calendar <strong>{{ calendarDropdownItems[0].title }}</strong>
                applies to all time ranges.
              </div>
              <template v-else>
                <div class="text-caption mb-2">
                  Verify the working-day calendar for each time range. Changing
                  one recalculates that range's end dates against the new
                  calendar.
                </div>
                <v-select
                  v-for="trt in trackingRangeTemplates"
                  :key="trt.id"
                  :model-value="calendarSelections[trt.id] || null"
                  :items="calendarDropdownItems"
                  item-title="label"
                  item-value="id"
                  :label="`Calendar for ${trt.title}`"
                  variant="outlined"
                  density="compact"
                  hide-details
                  class="mb-2"
                  @update:model-value="onCalendarChange(trt.id, $event)"
                />
              </template>
            </template>
          </v-alert>

          <!-- Which time ranges the NEW tasks in this import may edit in
               RDrive (lock.editableTimeRangeTemplateIds). Existing tasks keep
               their current locks; the page stamps the confirmed selection
               onto new tasks at import. -->
          <v-alert
            v-if="timeRangeEditability && timeRangeEditability.length > 0"
            type="info"
            variant="tonal"
            density="compact"
            class="mb-4 editable-ranges-alert"
          >
            <div class="text-caption mb-1">
              Confirm which time ranges stay editable in RDrive on the
              <strong>new</strong> tasks in this import. Existing tasks keep
              their current setting.
            </div>
            <div
              v-for="entry in timeRangeEditability"
              :key="entry.timeRangeTemplateId"
              class="d-flex align-center"
            >
              <v-switch
                :model-value="editableSelections[entry.timeRangeTemplateId] === true"
                color="primary"
                density="compact"
                hide-details
                :label="`${entry.title} editable`"
                @update:model-value="onEditableChange(entry.timeRangeTemplateId, $event)"
              />
              <v-chip
                v-if="entry.state === 'mixed'"
                size="x-small"
                color="warning"
                label
                class="ml-2"
              >
                existing tasks differ
              </v-chip>
            </div>
          </v-alert>

          <v-text-field
            v-model="commitTitle"
            label="Commit Title"
            outlined
            dense
            :rules="[v => !!v || 'Commit title is required']"
            required
          ></v-text-field>

          <v-textarea
            v-model="commitMessage"
            label="Commit Message"
            outlined
            dense
          ></v-textarea>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn color="grey" :disabled="saving" @click="importDialog = false">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            :loading="saving"
            :disabled="!commitTitle.trim() || saving"
            @click="$emit('import')"
          >
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Updated changes dialog -->
    <v-dialog v-model="changesDialog" max-width="1000">
      <v-card>
        <v-card-title class="text-h6 px-4 pt-4">
          Changes for: {{ selectedTask?.title }}
        </v-card-title>

        <v-card-text>
          <v-list>
            <v-list-subheader>Task Changes</v-list-subheader>
            <v-list-item v-for="(change, index) in getTaskChanges(selectedTask?.id)" :key="`task-${index}`">
              <v-list-item-title>{{ change }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="changesDialog = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- P6 import notes: shown above the review card so they don't sit
         between the toolbar and the gantt. Dismissible; re-shown whenever a
         new processing result arrives (see the watch below). -->
    <v-alert
      v-if="relationshipSummary && relationshipSummary.total > 0"
      v-model="relationshipAlertOpen"
      type="info"
      variant="tonal"
      density="compact"
      closable
      class="mb-2"
    >
      {{ relationshipSummary.imported }} of {{ relationshipSummary.total }} P6 relationships imported as finish-to-start prerequisites.
      <template v-if="droppedRelationshipParts.length">
        Not imported: {{ droppedRelationshipParts.join(', ') }} — RDrive dependencies support finish-to-start without lag only.
      </template>
    </v-alert>
    <v-alert
      v-if="prunedEmptyWbsCount > 0"
      v-model="prunedWbsAlertOpen"
      type="info"
      variant="tonal"
      density="compact"
      closable
      class="mb-2"
    >
      {{ prunedEmptyWbsCount }} empty P6 WBS {{ prunedEmptyWbsCount === 1 ? 'node was' : 'nodes were' }} skipped — they contain no activities, so they have no dates to import. They will be created by a future import once the export includes activities beneath them.
    </v-alert>
    <v-alert
      v-if="durationSummary && durationSummary.compared > 0"
      v-model="durationAlertOpen"
      :type="durationSummary.mismatched > 0 ? 'warning' : 'success'"
      variant="tonal"
      density="compact"
      closable
      class="mb-2"
    >
      <template v-if="durationSummary.mismatched > 0">
        {{ durationSummary.mismatched }} of {{ durationSummary.compared }} activity durations differ from the duration P6 carries for the activity (actual plus remaining hours), so those activities will not walk back to their P6 finish dates on the imported calendars.
        <template v-if="durationMismatchCalendarParts.length">
          Calendars involved: {{ durationMismatchCalendarParts.join('; ') }}.
        </template>
        <template v-if="durationSampleParts.length">
          Examples: {{ durationSampleParts.join('; ') }}.
        </template>
      </template>
      <template v-else>
        All {{ durationSummary.compared }} activity durations match the durations P6 carries for them.
      </template>
      <template v-if="durationSummary.skippedInProgress">
        {{ durationSummary.skippedInProgress }} in-progress {{ durationSummary.skippedInProgress === 1 ? 'activity was' : 'activities were' }} not compared — P6 records their actual duration independently of the calendar.
      </template>
    </v-alert>

    <v-card>
      <v-card-text class="pa-0">

      <v-toolbar color="primary" density="compact" >
        <v-text-field
          v-model="searchQuery"
          label="Search tasks"
          prepend-inner-icon="mdi-magnify"
          clearable
          @input="filterTasks"
          density="compact"
          hide-details

          class="mx-2"
          style="max-width: 300px;"
        ></v-text-field>

        <v-switch
          v-model="showChangesOnly"
          label="Show Changes Only"
          @change="filterTasks"
          density="compact"
          hide-details
          class="mx-2"
        ></v-switch>

        <v-spacer></v-spacer>

        <v-btn-toggle
          v-model="viewMode"
          mandatory
          density="compact"
          class="mx-2"
        >
          <v-btn value="tree" icon>
            <v-icon>mdi-file-tree</v-icon>
          </v-btn>
          <v-btn value="gantt" icon :disabled="!usesDates">
            <v-icon>mdi-chart-gantt</v-icon>
          </v-btn>
        </v-btn-toggle>

        <v-divider vertical class="mx-2"></v-divider>

        <v-btn icon class="mx-1" @click="expandAll" :title="'Expand All'">
          <v-icon>mdi-unfold-more-horizontal</v-icon>
        </v-btn>

        <v-btn icon class="mx-1" @click="collapseAll" :title="'Collapse All'">
          <v-icon>mdi-unfold-less-horizontal</v-icon>
        </v-btn>

        <template v-if="viewMode === 'gantt'">
          <v-divider vertical class="mx-2"></v-divider>

          <v-btn icon class="mx-1" @click="zoomIn">
            <v-icon>mdi-magnify-plus</v-icon>
          </v-btn>

          <v-btn icon class="mx-1" @click="zoomOut">
            <v-icon>mdi-magnify-minus</v-icon>
          </v-btn>

          <v-btn icon class="mx-1" @click="zoomToFit" :title="'Zoom to Fit'">
            <v-icon>mdi-fit-to-page</v-icon>
          </v-btn>
        </template>
      </v-toolbar>
        <!-- Height is measured against the visible screen rather than sized
             from 100vh: the dashboard iframe is far taller than the screen,
             so a vh-sized gantt pushes the Save Tasks bar below it.
             contain: inline-size keeps the gantt's intrinsic width (fixed-px
             locked columns + the timeline) from propagating up: <v-app> sits
             in a flex <v-layout> and can't shrink below its content's
             min-content, so with dates the whole app grew thousands of px
             wide and pushed the RPilot sidebar and Save Tasks off-screen. -->
        <div
          ref="ganttViewport"
          :style="{ position: 'relative', height: ganttHeight + 'px', width: '100%', overflow: 'hidden', contain: 'inline-size' }"
        >
          <div ref="ganttContainer" style="height:100%; width:100%"></div>
          <sub-task-panel
            v-model="subtaskPanelOpen"
            :task="subtaskPanelTask"
            :teams="resourceStore.getTeams"
          />
        </div>
      </v-card-text>
      <v-card-actions ref="actionsRow">
        <v-btn @click="$emit('back')">
          Back
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          :disabled="!props.nestedTasks.length"
          @click="openImportDialog"
        >
          Save Tasks
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-stepper-window-item>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted, computed, nextTick, markRaw, toRaw } from 'vue';
import { useScheduleStore } from '@/stores/schedule';
import { useResourceStore } from '@/stores/resource';
import SubTaskPanel from '@components/SubTaskPanel.vue';
import { formatTaskDuration, toBryntumDuration } from '@/utils/durationDisplay';
import { resolvePublishedCalendar } from '@/utils/CalendarEndDateCalculator';
import { matchP6Calendar } from '@/utils/P6CalendarMatch';
import { computeTimeRangeEndDate } from '@/utils/CalendarEndDateCalculator';
import { fitHeightToVisibleViewport } from '@/utils/visibleViewportHeight';
import { Gantt, DateHelper, Dependencies } from '@bryntum/gantt';

import '@bryntum/gantt/gantt.material.css';
import '@bryntum/gantt/gantt.stockholm.css';

// Gantt sizing (see measureGanttHeight)
const MIN_GANTT_HEIGHT = 320;        // never collapse below a usable gantt
const FALLBACK_ACTIONS_HEIGHT = 52;  // v-card-actions, before it is measured
const PAGE_BOTTOM_GUTTER = 24;       // content-container padding + breathing room

const props = defineProps({
  nestedTasks: {
    type: Array,
    required: true
  },
  displayTasks: {
    type: Array,
    required: true
  },
  changes: {
    type: Object,
    required: true
  },
  selectedSchedule: {
    type: Object,
    required: true
  },
  // P6 relationship import summary ({ total, imported, droppedByType, ... }
  // from P6RelationshipParser); null for Excel uploads.
  relationshipSummary: {
    type: Object,
    default: null
  },
  // WBS nodes dropped at parse time for having no activities beneath them;
  // 0 for Excel uploads.
  prunedEmptyWbsCount: {
    type: Number,
    default: 0
  },
  // Committed working-time durations checked against the durations P6
  // carries for the activities ({ compared, matched, mismatched,
  // skippedInProgress, calendars, samples } from XMLTaskParser); null for
  // Excel uploads.
  durationSummary: {
    type: Object,
    default: null
  },
  // Calendars parsed from the P6 file (XMLTaskParser.parseCalendars); empty
  // for Excel uploads. They are created/updated on import, so durations are
  // evaluated with what is about to be published (see calendarById).
  p6Calendars: {
    type: Array,
    default: () => []
  },
  stepValue: {
    type: String,
    default: '3'
  },
  // The pending RPilot create proposal's newSchedule metadata (title, code,
  // scheduleTemplateId, timeRangeCalendars). Null on update flows and manual
  // imports — the template/calendar verification panel only renders on create.
  newScheduleProposal: {
    type: Object,
    default: null
  },
  // Per-time-range-template editability for the NEW tasks in this import:
  // [{ timeRangeTemplateId, title, state }] with state 'editable' | 'locked'
  // | 'mixed' ('mixed' = existing tasks disagree, so the user must confirm).
  // Null hides the confirmation panel (e.g. an update that adds no tasks).
  timeRangeEditability: {
    type: Array,
    default: null
  },
  // True while the parent is committing the confirmed tasks: the Confirm
  // button shows a spinner and the dialog can't be dismissed mid-request.
  saving: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:changes', 'update:nested-tasks', 'update:time-range-calendars', 'update:editable-time-range-ids']);
const scheduleStore = useScheduleStore();
const resourceStore = useResourceStore();

const subtaskPanelOpen = ref(false);
const subtaskPanelTask = ref(null);

const changesDialog = ref(false);
const selectedTask = ref(null);
const ganttContainer = ref(null);
let gantt = null;

// Gantt sizing: see measureGanttHeight below.
const stepRoot = ref(null);
const ganttViewport = ref(null);
const actionsRow = ref(null);
const ganttHeight = ref(MIN_GANTT_HEIGHT);
let layoutObserver = null;
let taskLookup = null;

const searchQuery = ref('');
const showChangesOnly = ref(false);

const importDialog = ref(false);
const commitTitle = computed({
  get: () => scheduleStore.getCommitTitle,
  set: (value) => scheduleStore.updateCommitTitle(value)
});

const commitMessage = computed({
  get: () => scheduleStore.getCommitMessage,
  set: (value) => scheduleStore.updateCommitMessage(value)
});

const usesDates = computed(() => {
  if (props.selectedSchedule) {
    if (props.selectedSchedule.timeRanges && props.selectedSchedule.timeRanges.length > 0) {
      const allNone = props.selectedSchedule.timeRanges.every(range => range.type === 'none');
      return !allNone;
    } else {
      const template = scheduleStore.scheduleTemplatesObj[props.selectedSchedule.scheduleTemplateId];
      if (template && template.length > 0 && template[0].timeRangeTemplates && template[0].timeRangeTemplates.length > 0) {
        const allNone = template[0].timeRangeTemplates.every(range => range.type === 'none');
        return !allNone;
      }
    }
  }
  return false;
});

// Default to gantt mode when dates exist, tree mode otherwise
const viewMode = ref(usesDates.value ? 'gantt' : 'tree');

// Dismissal state of the P6 import note alerts. Dismissing is per result:
// a new file (new summary / pruned count) brings the notes back.
const relationshipAlertOpen = ref(true);
const prunedWbsAlertOpen = ref(true);
const durationAlertOpen = ref(true);
watch(() => [props.relationshipSummary, props.prunedEmptyWbsCount, props.durationSummary], () => {
  relationshipAlertOpen.value = true;
  prunedWbsAlertOpen.value = true;
  durationAlertOpen.value = true;
});

const roundHours = (hours) => Math.round(hours * 100) / 100;

// Calendars with duration mismatches, worst first; a calendar whose work
// week was assumed (no work periods in the export) is the usual culprit.
const durationMismatchCalendarParts = computed(() =>
  (props.durationSummary?.calendars || [])
    .filter(entry => entry.mismatched > 0)
    .map(entry => `${entry.title || 'unknown calendar'} (${entry.mismatched} of ${entry.total}${entry.assumedWorkWeek ? ', work week assumed' : ''})`));

const durationSampleParts = computed(() =>
  (props.durationSummary?.samples || [])
    .map(sample => `${sample.code}: P6 ${roundHours(sample.p6Hours)} h, imported ${roundHours(sample.importedHours)} h`));

// Human-readable buckets of P6 relationships that were not imported.
const droppedRelationshipParts = computed(() => {
  const summary = props.relationshipSummary;
  if (!summary) return [];
  const parts = Object.entries(summary.droppedByType || {})
    .map(([type, count]) => `${count} ${type}`);
  if (summary.droppedWithLag) parts.push(`${summary.droppedWithLag} Finish to Start with lag`);
  if (summary.crossProject) parts.push(`${summary.crossProject} cross-project`);
  if (summary.unresolved) parts.push(`${summary.unresolved} unresolvable`);
  if (summary.duplicates) parts.push(`${summary.duplicates} duplicate`);
  return parts;
});

const showChanges = (item) => {
  selectedTask.value = item;
  changesDialog.value = true;
};

// The Bryntum record only carries the count; the subtasks array lives on the
// raw task in the lookup.
const showSubtasks = (record) => {
  subtaskPanelTask.value = taskLookup?.get(record.id) || null;
  subtaskPanelOpen.value = !!subtaskPanelTask.value;
};


const newCount = computed(() => countChangeType('new'));
const updatedCount = computed(() => countChangeType('updated'));
const deletedCount = computed(() => countChangeType('deleted'));

/**
 * Build a flat lookup Map from the full task tree for O(1) child retrieval.
 * Uses toRaw() to unwrap Vue proxies before passing data to Bryntum.
 */
const buildTaskLookup = (tasks) => {
  const lookup = new Map();
  const walk = (list) => {
    for (const task of list) {
      const raw = toRaw(task);
      lookup.set(raw.id, raw);
      if (raw.children && raw.children.length > 0) {
        walk(raw.children);
      }
    }
  };
  walk(tasks);
  return lookup;
};

// Dependencies only move dates on 'dependencyTracking' ("Planned") ranges —
// snagr-web's TaskViewer marks records inactive for every other range type so
// arrows still draw but the engine leaves the proposed dates alone. The gantt
// renders timeRanges[0], so that row's type decides.
const displayedRangeIsDependencyTracking = (tasks) => {
  for (const task of tasks) {
    const type = task.timeRanges?.[0]?.timeRangeTemplateType;
    if (type) return type === 'dependencyTracking';
    if (task.children?.length) {
      const fromChildren = displayedRangeIsDependencyTracking(task.children);
      if (fromChildren !== null) return fromChildren;
    }
  }
  return null;
};

/**
 * Flatten the tasks' prerequisiteTaskIds into Bryntum dependency records —
 * the same record shape snagr-web's TaskViewer derives (`${from}-${to}` id,
 * from/to, active by range type). Type is left at Bryntum's default
 * (2 = finish-to-start) — the only dependency semantic RDrive stores.
 */
const buildDependencyRecords = (tasks) => {
  const rawTasks = toRaw(tasks);
  const active = displayedRangeIsDependencyTracking(rawTasks) === true;
  // Collect ids first: a prerequisite pointing outside the displayed tree
  // (e.g. filtered or partially loaded data) must not produce a dangling
  // record Bryntum can't resolve.
  const ids = new Set();
  const collect = (list) => {
    for (const task of list) {
      ids.add(toRaw(task).id);
      if (task.children?.length) collect(task.children);
    }
  };
  collect(rawTasks);

  const records = [];
  const walk = (list) => {
    for (const task of list) {
      const raw = toRaw(task);
      (raw.prerequisiteTaskIds || []).forEach(predecessorId => {
        if (!ids.has(predecessorId)) return;
        records.push({
          id: `${predecessorId}-${raw.id}`,
          from: predecessorId,
          to: raw.id,
          active,
          // The review gantt never edits links — proposals and spreadsheets do.
          readOnly: true,
        });
      });
      if (raw.children && raw.children.length > 0) {
        walk(raw.children);
      }
    }
  };
  walk(rawTasks);
  return records;
};

// Published calendars that the file's P6 calendars will update on import
// (matched the way CalendarImporter matches: p6-id tag, then code), each
// overlaid with the parsed factors/intervals/timezone so the grid shows the
// durations the import is about to produce rather than the previous import's
// copy (e.g. a 7-day calendar published with the old fixed 20 d/mo reads a
// 6-year span as 9 years). One object per published id so the walk's
// per-calendar memo still applies across rows.
const p6OverlaidCalendars = computed(() => {
  const overlays = new Map();
  const published = scheduleStore.getCalendars || [];
  (props.p6Calendars || []).forEach((p6Calendar) => {
    const match = matchP6Calendar(p6Calendar, published);
    if (!match || overlays.has(match.calendar.id)) return;
    overlays.set(match.calendar.id, {
      ...match.calendar,
      hoursPerDay: p6Calendar.hoursPerDay,
      daysPerWeek: p6Calendar.daysPerWeek,
      daysPerMonth: p6Calendar.daysPerMonth,
      timeZone: p6Calendar.timeZone,
      intervals: p6Calendar.intervals
    });
  });
  return overlays;
});

// The calendar a row's duration is evaluated against — same resolution as the
// commit walk (id, then the current revision of the same version family),
// with the file's pending P6 version preferred when there is one.
const calendarById = (calendarId, calendarVersionReference = null) => {
  const published = resolvePublishedCalendar(scheduleStore.getCalendars, { calendarId, calendarVersionReference });
  if (!published) return null;
  return p6OverlaidCalendars.value.get(published.id) || published;
};

// ── New-schedule verification panel (RPilot create proposals only) ─────────

const newScheduleTemplate = computed(() => {
  const templateId = props.newScheduleProposal?.scheduleTemplateId;
  return templateId ? (scheduleStore.scheduleTemplatesObj?.[templateId]?.[0] || null) : null;
});

const newScheduleTemplateTitle = computed(() =>
  newScheduleTemplate.value?.title || props.newScheduleProposal?.scheduleTemplateId || 'Unknown'
);

const trackingRangeTemplates = computed(() =>
  (newScheduleTemplate.value?.timeRangeTemplates || []).filter((trt) => trt.type !== 'none')
);

const calendarDropdownItems = computed(() =>
  (scheduleStore.getCalendars || []).map((cal) => ({ ...cal, label: `${cal.title} (${cal.code})` }))
);

// Per-time-range calendar picks, seeded from the proposal's (agent-confirmed)
// mapping whenever a new proposal lands.
const calendarSelections = ref({});
watch(() => props.newScheduleProposal, (proposal) => {
  const selections = {};
  (proposal?.timeRangeCalendars || []).forEach((m) => {
    selections[m.timeRangeTemplateId] = m.calendarId;
  });
  calendarSelections.value = selections;
}, { immediate: true });

// Per-time-range editability toggles for new tasks, seeded from the page's
// derivation: 'locked' pre-selects off, 'editable' and 'mixed' pre-select on
// (mixed additionally flags that existing tasks disagree). The parent always
// holds the current selection via update:editable-time-range-ids.
const editableSelections = ref({});
const emitEditableTimeRangeIds = () => {
  emit('update:editable-time-range-ids', (props.timeRangeEditability || [])
    .map((entry) => entry.timeRangeTemplateId)
    .filter((templateId) => editableSelections.value[templateId] === true));
};
watch(() => props.timeRangeEditability, (entries) => {
  const selections = {};
  (entries || []).forEach((entry) => {
    selections[entry.timeRangeTemplateId] = entry.state !== 'locked';
  });
  editableSelections.value = selections;
  emitEditableTimeRangeIds();
}, { immediate: true, deep: true });

const onEditableChange = (timeRangeTemplateId, value) => {
  editableSelections.value = { ...editableSelections.value, [timeRangeTemplateId]: !!value };
  emitEditableTimeRangeIds();
};

// The user overrode a time range's calendar: restamp every matching row in
// both trees and recompute each row's end date against the new working-day
// calendar (the commit stores endDate verbatim — nothing downstream derives
// it), then rebuild the gantt rows and tell the parent.
const onCalendarChange = (timeRangeTemplateId, calendarId) => {
  const calendar = calendarById(calendarId);
  if (!calendar) return;
  calendarSelections.value = { ...calendarSelections.value, [timeRangeTemplateId]: calendarId };

  const restamp = (tasks) => {
    for (const task of tasks) {
      for (const row of task.timeRanges || []) {
        if (row.timeRangeTemplateId !== timeRangeTemplateId) continue;
        row.calendarId = calendar.id;
        row.calendarVersionReference = calendar.versionReference ?? null;
        const endDate = computeTimeRangeEndDate(row, calendar);
        if (endDate) row.endDate = endDate;
      }
      if (task.children?.length) restamp(task.children);
    }
  };
  restamp(props.nestedTasks);
  restamp(props.displayTasks);

  emit('update:nested-tasks', [...props.nestedTasks]);
  emit('update:time-range-calendars', trackingRangeTemplates.value
    .map((trt) => {
      const cal = calendarById(calendarSelections.value[trt.id]);
      return cal ? {
        timeRangeTemplateId: trt.id,
        calendarId: cal.id,
        calendarVersionReference: cal.versionReference ?? null,
      } : null;
    })
    .filter(Boolean));

  if (gantt) {
    taskLookup = buildTaskLookup(props.displayTasks);
    gantt.taskStore.clearFilters(true);
    gantt.project.taskStore.rootNode.replaceChildren(transformRootTasks(props.displayTasks));
    gantt.project.dependencyStore.data = buildDependencyRecords(props.displayTasks);
    filterTasks();
  }
};

const transformTask = (task) => {
  const timeRange = task.timeRanges?.[0];

  const dur = resolveDuration(timeRange);
  const calendar = calendarById(timeRange?.calendarId, timeRange?.calendarVersionReference);
  const bryntumDur = toBryntumDuration(timeRange, dur, calendar);

  return {
    id: task.id,
    title: task.title,
    code: task.code,
    progress: timeRange?.progress || 0,
    startDate: timeRange?.startDate || null,
    duration: bryntumDur.value,
    durationUnit: bryntumDur.unit,
    // What the main web app's gantt shows for this task after commit — the
    // working time between its dates on its calendar. The grid column and
    // tooltip both use it so the task reads the same value everywhere.
    durationDisplay: formatTaskDuration(timeRange, dur, calendar),
    calendarId: timeRange?.calendarId || null,
    calendarDisplay: describeCalendar(calendar, timeRange?.calendarId),
    subtasksCount: task.subtasks?.length || 0,
    leaf: !task.children?.length,
    expanded: false,
    changeType: displayChangeType(task.id),
    changeCount: getChangeCount(task.id),
    subtaskChangeCount: getSubtaskChangeCount(task.id),
    manuallyScheduled: true,
    draggable: false,
    sort: task.sort
  };
};

const buildBryntumCalendars = () => {
  const calendars = scheduleStore.getCalendars || [];
  return calendars.map(cal => {
    const intervals = (cal.intervals || []).map(interval => {
      const entry = {
        isWorking: interval.isWorking !== false
      };
      if (Number.isFinite(interval.priority)) entry.priority = interval.priority;
      if (interval.recurrentStartDate) {
        entry.recurrentStartDate = interval.recurrentStartDate;
        entry.recurrentEndDate = interval.recurrentEndDate;
      } else if (interval.startDate) {
        entry.startDate = interval.startDate;
        entry.endDate = interval.endDate;
      }
      return entry;
    });

    if (intervals.length === 0) {
      const daysPerWeek = cal.daysPerWeek || 5;
      const hoursPerDay = cal.hoursPerDay || 8;
      const workDays = daysPerWeek >= 6 ? '1-6' : daysPerWeek >= 7 ? '0-6' : '1-5';
      intervals.push(
        { recurrentStartDate: `0 0 * * ${workDays}`, recurrentEndDate: `0 ${hoursPerDay} * * ${workDays}`, isWorking: true }
      );
    }

    return {
      id: cal.id,
      name: cal.title,
      intervals,
      hoursPerDay: cal.hoursPerDay || 8,
      daysPerWeek: cal.daysPerWeek || 5,
      daysPerMonth: cal.daysPerMonth || 20
    };
  });
};

// The calendar a row's duration is evaluated against, with the factors that
// decide how its working hours read as days/months/years ("24h/d 20d/mo"
// explains a 9-year figure for a 6-year span). An unresolved id is shown as
// such so a missing/unpublished calendar is visible rather than silently
// falling back.
const describeCalendar = (calendar, calendarId) => {
  if (!calendar) return calendarId ? `${calendarId} (not published)` : '';
  const factors = `${calendar.hoursPerDay || 8}h/d ${calendar.daysPerMonth || 20}d/mo`;
  return `${calendar.title || calendar.code || calendar.id} · ${factors}`;
};

const resolveDuration = (timeRange) => {
  if (!timeRange) return { value: null, unit: 'day' };
  if (timeRange.duration && timeRange.duration.value !== undefined) {
    return timeRange.duration;
  }
  if (timeRange.durationInfo) {
    return { value: timeRange.durationInfo.duration, unit: timeRange.durationInfo.durationUnit || 'day' };
  }
  return { value: null, unit: 'day' };
};

/**
 * Transform root-level tasks only (children are lazy-loaded).
 */
const transformRootTasks = (tasks) => {
  return toRaw(tasks).map(task => {
    const raw = toRaw(task);
    const node = transformTask(raw);
    // Mark as non-leaf if it has children, but don't load them yet
    if (raw.children && raw.children.length > 0) {
      node.leaf = false;
      node.children = true; // Bryntum: true = "has children, load on demand"
    }
    return node;
  });
};

/**
 * Load children for a parent node from the taskLookup.
 */
const loadChildrenForNode = (record) => {
  if (!taskLookup) return;
  const rawTask = taskLookup.get(record.id);
  if (!rawTask || !rawTask.children) return;

  const childNodes = rawTask.children.map(child => {
    const node = transformTask(toRaw(child));
    if (child.children && child.children.length > 0) {
      node.leaf = false;
      node.children = true;
    }
    return node;
  });

  record.appendChild(childNodes);
};

const initGantt = () => {
  // Build flat lookup for lazy loading
  taskLookup = buildTaskLookup(props.displayTasks);
  const dependencyRecords = buildDependencyRecords(props.displayTasks);

  // Define base columns that are always shown
  const baseColumns = [
    { text:'Code', type:'name', field: 'code', readOnly:true, treeColumn: true, width:100 },
    { text: 'Title', field: 'title', width: 250, readOnly:true, },
    {
      field: 'changes',
      text: 'Changes',
      type: 'template',
      readOnly:true,
      width: 120,
      template: ({ record }) => {
        const color = getFullBackgroundColor(record.changeType);
        const changeCount = getChangeCount(record.id);
        return `<div class="gantt-chip" style="background-color: ${color};">${changeCount}</div>`;
      }
    },
    {
      field: 'subtasksCount',
      text: 'Sub-tasks',
      type: 'template',
      readOnly: true,
      width: 110,
      template: ({ record }) => {
        const count = record.subtasksCount || 0;
        if (!count) {
          return '';
        }
        const changed = getSubtaskChangeCount(record.id) > 0 ? ' gantt-chip--subtasks-changed' : '';
        return `<div class="gantt-chip gantt-chip--subtasks${changed}">${count}</div>`;
      }
    }
  ];

  // Date-related columns to add when usesDates is true
  const dateColumns = [
    {
      type: 'date',
      text: 'Start Date',
      field: 'startDate',
      width: 130,
      format: 'YYYY-MM-DD',
      readOnly:true,
    },
    {
      text: 'Duration',
      field: 'durationDisplay',
      readOnly:true,
      width: 100,
    },
    {
      text: 'Calendar',
      field: 'calendarDisplay',
      readOnly: true,
      width: 200,
    },
  ];

  // Combine columns based on usesDates value
  const columns = usesDates.value ? [...baseColumns, ...dateColumns] : baseColumns;

  const isTreeMode = viewMode.value === 'tree';

  const config = {
    appendTo: ganttContainer.value,
    scrollTaskIntoViewOnCellClick : true,
    columns,
    features: {
      taskEdit: false,
      // Stock tooltip layout, but with the duration the main web app's gantt
      // shows for the committed task (backend-humanized working duration,
      // e.g. "1.5 years") instead of Bryntum's elapsed-days rendering.
      taskTooltip: {
        template({ taskRecord }) {
          const formatDate = (date) => date
            ? DateHelper.format(date, 'MMM D, YYYY')
            : '';
          // API progress is a 0-1 fraction (snagr-web's normalizedProgress
          // does the same x100 before display).
          const progress = taskRecord.progress ?? 0;
          const percent = progress <= 1 ? progress * 100 : progress;
          const rows = [
            ['Start', formatDate(taskRecord.startDate)],
            ['End', formatDate(taskRecord.endDate)],
            ['Duration', taskRecord.durationDisplay || ''],
            ['Complete', `${Math.round(percent)}%`]
          ];
          return `<table class="b-tooltip-table">${rows
            .map(([label, value]) => `<tr><td>${label}:</td><td>${value}</td></tr>`)
            .join('')}</table>`;
        }
      },
      // Prerequisite links are display-only here: draw them, never allow
      // creating new ones (proposals and spreadsheets are the write paths).
      // Always enabled — an RPilot proposal can introduce dependencies after
      // the gantt mounts, so gating on the initial records would never draw
      // them. Tooltip mirrors snagr-web's TaskViewer: the stock from/to
      // layout with "<code> <title>" task labels patched in.
      dependencies: {
        allowCreate: false,
        tooltipTemplate(dependency) {
          const content = Dependencies.configurable.tooltipTemplate.call(this, dependency);
          const label = (taskRecord) => taskRecord
            ? [taskRecord.code, taskRecord.title].filter(Boolean).join(' ')
            : '';
          const taskLabels = [label(dependency.fromEvent), label(dependency.toEvent)];
          content.forEach((item, index) => {
            if (item?.className?.split(' ').includes('b-sch-box')) {
              content[index - 1].text = taskLabels.shift();
            }
          });
          return content;
        },
      },
      rowReorder: false,
      filter: true,
      eventResizeFeature: false,

      taskMenu: {
        processItems({ items }) {
          const deleteTask = items.deleteTask;

          for (const key in items) {
            if (Object.prototype.hasOwnProperty.call(items, key)) {
              delete items[key];
            }
          }

          if (deleteTask) {
            deleteTask.text = 'End Task';
            deleteTask.onItem = ({ record }) => {
              gantt.project.taskStore.remove(record);
              deleteTasks(record.data.id);
            }
            items.endTask = deleteTask;

          } else {
            items.endTask = {
              text: 'End Task',
              icon: 'b-icon b-icon-trash',
              onItem: ({ record }) => {
                gantt.project.taskStore.remove(record);
                deleteTasks(record.data.id);
              }
            };
          }

          return items;
        }
      }
    },
    project: {
      calendars: buildBryntumCalendars(),
      dependencyStore: { data: dependencyRecords },
      taskStore: {
        tree: true,
        fields: ['id', 'title', 'code', 'subtasksCount', 'subtaskChangeCount', 'startDate', 'duration', 'durationUnit', 'durationDisplay', 'progress', 'calendarId', 'calendarDisplay'],
        rootNode: {
          expanded: true,
          children: transformRootTasks(props.displayTasks)
        },
      }
    },
    listeners: {
      beforeToggleNode({ record }) {
        // Lazy-load children when expanding a node for the first time
        if (!record.isExpanded(gantt.taskStore) && isUnloadedParent(record)) {
          loadChildrenForNode(record);
        }
      },
      cellClick: ({ record, column}) => {
        if (column.field === 'changes' && record.changeCount > 0) {
          showChanges(record);
        }
        if (column.field === 'subtasksCount' && record.subtasksCount > 0) {
          showSubtasks(record);
        }
      }
    },
    onRenderRow: ({ row, record }) => {
      const backgroundColor = getBackgroundColor(record.changeType) || '';
      if (row.elements.locked) {
        row.elements.locked.style.backgroundColor = backgroundColor;
      }
      if (row.elements.normal) {
        row.elements.normal.style.backgroundColor = backgroundColor;
      }
    }
  };

  // If tree mode or no dates, hide the timeline region
  if (isTreeMode || !usesDates.value) {
    config.subGridConfigs = {
      locked: { width: '100%' },
      normal: { hidden: true }
    };
  }

  gantt = markRaw(new Gantt(config));
};

const destroyGantt = () => {
  if (gantt) {
    gantt.destroy();
    gantt = null;
  }
};

// A node planted with `children: true` whose children haven't been appended
// yet. Bryntum leaves `record.data.children === true` in place after
// appendChild(), so the raw data is not a usable "already loaded" signal —
// only the record's own children array is. Checking data would reload (and
// id-collide on) every parent on each pass.
const isUnloadedParent = (record) => record.isParent && !record.isLoaded;

// Materialize every lazily-marked node (children === true) into real records.
// Required before any whole-tree store operation — expandAll and filtering
// both traverse children and crash on the boolean lazy markers.
const loadAllUnloadedChildren = () => {
  if (!gantt) return;
  const walk = (node) => {
    if (isUnloadedParent(node)) {
      loadChildrenForNode(node);
    }
    // While a filter is active `children` is the filtered subset; the full
    // set lives in unfilteredChildren.
    const children = node.unfilteredChildren || node.children;
    if (Array.isArray(children)) {
      for (const child of children) {
        walk(child);
      }
    }
  };
  walk(gantt.taskStore.rootNode);
};

const expandAll = () => {
  if (!gantt) return;
  loadAllUnloadedChildren();
  gantt.expandAll();
};

const collapseAll = () => {
  if (gantt) {
    gantt.collapseAll();
  }
};

const zoomIn = () => {
  if (gantt) {
    gantt.zoomIn();
  }
};

const zoomOut = () => {
  if (gantt) {
    gantt.zoomOut();
  }
};

const zoomToFit = () => {
  if (gantt) {
    gantt.zoomToFit({
      leftMargin: 50,
      rightMargin: 50
    });
  }
};

const getTaskChanges = (taskId) => {
  if (!taskId || !props.changes[taskId]) return [];
  return props.changes[taskId].changes || [];
};

const getChangeType = (taskId) => {
  if (!taskId || !props.changes[taskId]) return null;
  return props.changes[taskId].changeType;
};

const getChangeCount = (taskId) => {
  if (!taskId || !props.changes[taskId]) return 0;
  const changes = props.changes[taskId];
  return changes.changes?.length || 0;
};

const getSubtaskChangeCount = (taskId) => {
  if (!taskId || !props.changes[taskId]) return 0;
  return props.changes[taskId].subtaskChanges?.length || 0;
};

// A task whose only proposed edits live in its subtasks keeps changeType
// 'updated' in the changes map (the commit needs it), but the row background
// and Changes column must not read as a task-level edit — the Sub-tasks chip
// carries the highlight instead.
const displayChangeType = (taskId) => {
  const changeType = getChangeType(taskId);
  if (changeType === 'updated' && getChangeCount(taskId) === 0) return null;
  return changeType;
};

const deleteTasks = (taskId) => {
  let removedTask = null;

  const removeTaskFromArray = (tasks) => {
    const taskIndex = tasks.findIndex(task => task.id === taskId);

    if (taskIndex !== -1) {
      removedTask = tasks[taskIndex];
      tasks.splice(taskIndex, 1);
      return true;
    }

    for (let i = 0; i < tasks.length; i++) {
      if (tasks[i].children && tasks[i].children.length > 0) {
        if (removeTaskFromArray(tasks[i].children)) {
          return true;
        }
      }
    }

    return false;
  };

  removeTaskFromArray(props.nestedTasks);

  // Surviving tasks may hold prerequisiteTaskIds pointing into the removed
  // subtree; the commit endpoint rejects prerequisites that don't resolve to
  // a task in the schedule, so drop those references here.
  if (removedTask) {
    const removedIds = new Set();
    const collectIds = (task) => {
      removedIds.add(task.id);
      (task.children || []).forEach(collectIds);
    };
    collectIds(removedTask);

    const scrubPrerequisites = (tasks) => {
      tasks.forEach(task => {
        if (task.prerequisiteTaskIds?.some(id => removedIds.has(id))) {
          task.prerequisiteTaskIds = task.prerequisiteTaskIds.filter(id => !removedIds.has(id));
        }
        if (task.children && task.children.length > 0) {
          scrubPrerequisites(task.children);
        }
      });
    };
    scrubPrerequisites(props.nestedTasks);
  }

  // The tree was edited in place; the parent holds it in a shallowRef, so
  // push a fresh array identity for bindings that read it (e.g. .length).
  emit('update:nested-tasks', [...props.nestedTasks]);

  const updatedChanges = { ...props.changes };

  if (!updatedChanges[taskId]) {
    updatedChanges[taskId] = {
      changeType: 'deleted',
      changes: ['Task marked for deletion']
    };
  } else {
    updatedChanges[taskId] = {
      ...updatedChanges[taskId],
      changeType: 'deleted'
    };

    const existingChanges = [...updatedChanges[taskId].changes];
    if (!existingChanges.includes('Task marked for deletion')) {
      updatedChanges[taskId].changes = [...existingChanges, 'Task marked for deletion'];
    }
  }

  emit('update:changes', updatedChanges);
};

const filterTasks = () => {
  if (!gantt) return;

  // No active filter → clear only if something is filtered. A non-silent
  // clearFilters always re-runs the store's filter pipeline, which traverses
  // children and crashes on lazy child markers (children === true), so
  // materialize the tree before letting it walk.
  if (!searchQuery.value && !showChangesOnly.value) {
    if (gantt.taskStore.isFiltered) {
      loadAllUnloadedChildren();
      gantt.taskStore.clearFilters();
    }
    return;
  }

  // Filtering matches against the whole tree, so unloaded branches must be
  // materialized first or matches inside them are invisible (and Bryntum's
  // traversal crashes on the boolean markers).
  loadAllUnloadedChildren();

  const query = searchQuery.value.toLowerCase();
  const searchFilter = ({ title, code }) =>
    !query ||
    (title || '').toLowerCase().includes(query) ||
    (code || '').toLowerCase().includes(query);

  const changesFilter = (task) =>
    !showChangesOnly.value || task.changeCount > 0 || task.subtaskChangeCount > 0;

  gantt.taskStore.filter(task => searchFilter(task) && changesFilter(task));
};

const countChangeType = (type) => {
  return Object.values(props.changes).filter(change => change.changeType === type).length;
};

const openImportDialog = () => {
  importDialog.value = true;
};
const closeImportDialog = () => {
  importDialog.value = false;
};

// Let the RPilot page open the commit dialog from its proposal banner — same
// dialog, same prefilled commit title/message, as the Save Tasks button — and
// close it once the commit has been sent.
defineExpose({ openImportDialog, closeImportDialog });

watch([searchQuery, showChangesOnly], () => {
  filterTasks();
});

// Watch for view mode changes - destroy and reinitialize Gantt
watch(viewMode, () => {
  subtaskPanelOpen.value = false;
  destroyGantt();
  nextTick(() => {
    initGantt();
  });
});

// Watch for displayTasks changes - rebuild lookup and refresh Gantt
watch(() => props.displayTasks, (newValue) => {
  if (newValue && newValue.length > 0) {
    taskLookup = buildTaskLookup(newValue);
    subtaskPanelOpen.value = false;
    if (gantt) {
      // Drop filter definitions silently (no re-filter pass) before swapping —
      // the outgoing tree may hold unmaterialized lazy markers the filter
      // traversal would crash on, and it's about to be discarded anyway.
      gantt.taskStore.clearFilters(true);
      // Bryntum generates no setter for the children field — a plain
      // `rootNode.children = [...]` assignment plants raw config objects that
      // blow up the next store traversal with "child.setData is not a
      // function". replaceChildren() ingests the configs into real records.
      gantt.project.taskStore.rootNode.replaceChildren(transformRootTasks(newValue));
      gantt.project.dependencyStore.data = buildDependencyRecords(newValue);
      filterTasks();
    }
  }
}, { immediate: true });

const getBackgroundColor = (changeType) => {
  switch (changeType) {
          case 'new':
            return '#e6ffe6'; // Light green
          case 'updated':
            return '#fff0e6'; // Light orange
          case 'deleted':
            return '#ffe6e6'; // Light red
          default:
          return; // Light blue - unchanged
        }
};

const getFullBackgroundColor = (changeType) => {
  switch (changeType) {
          case 'new':
            return '#8bc34a'; // green
          case 'updated':
            return '#ff9800'; // orange
          case 'deleted':
            return '#c70000'; // red
          default:
            return '#3ca3f4'; // blue
        }
};

// The gantt fills what is left of the VISIBLE screen under everything above
// it, keeping the Save Tasks bar below it in view. It cannot be sized from
// 100vh: the app is embedded in a dashboard iframe the shell sizes from a
// per-dashboard height attribute, routinely much taller than the screen, so a
// vh-sized gantt grew until the actions bar sat off-screen (visible only by
// zooming the browser out). Measuring the viewport's own top also picks up the
// P6 import alerts and the toolbar without hard-coding their heights.
const measureGanttHeight = () => {
  const viewport = ganttViewport.value;
  if (!viewport) return;

  const actionsEl = actionsRow.value?.$el || actionsRow.value;
  const reserveBelow = (actionsEl?.offsetHeight || FALLBACK_ACTIONS_HEIGHT) + PAGE_BOTTOM_GUTTER;
  const next = fitHeightToVisibleViewport({
    top: viewport.getBoundingClientRect().top,
    reserveBelow,
    minHeight: MIN_GANTT_HEIGHT
  });

  // Only assign on a real change: the ResizeObserver below watches an element
  // that contains the gantt, so an unconditional write would re-trigger it.
  if (Math.abs(next - ganttHeight.value) >= 1) {
    ganttHeight.value = next;
  }
};

onMounted(() => {
  nextTick(() => {
    measureGanttHeight();
    initGantt();
    const rootEl = stepRoot.value?.$el || stepRoot.value;
    if (rootEl && typeof ResizeObserver !== 'undefined') {
      // Alerts appearing/being dismissed, the toolbar wrapping, and the shell
      // resizing the iframe all move the gantt's top edge.
      layoutObserver = new ResizeObserver(() => measureGanttHeight());
      layoutObserver.observe(rootEl);
    }
    window.addEventListener('resize', measureGanttHeight);
  });
});

onUnmounted(() => {
  window.removeEventListener('resize', measureGanttHeight);
  layoutObserver?.disconnect();
  layoutObserver = null;
  destroyGantt();
});
</script>

<style>
/* Unscoped: the dialog teleports to the overlay container outside this
   component's DOM. The app runs in an iframe the parent sizes to content, so
   the iframe viewport can be far taller than the visible screen. Vuetify
   centres the dialog in that tall viewport and caps it at calc(100% - 48px),
   which lets the Save Tasks card (chips + new-schedule calendars + editable
   ranges + commit fields) grow until the Cancel/Confirm actions sit below
   the visible screen. Anchor to the top and cap the height in px so the
   actions stay reachable; `scrollable` keeps the title and actions pinned and
   scrolls the card text. The cap goes on the overlay content because
   Vuetify's scrollable rule (.v-dialog--scrollable > .v-overlay__content >
   .v-card { max-height: 100% }) outranks a cap on the card itself. `top`
   overrides the app-wide `.v-dialog .v-overlay__content { top: 20vh }` from
   WelcomeDialog, which is 20% of the (tall) iframe, not of the screen.
   Same approach as CalendarImportDialog. */
.v-dialog > .v-overlay__content.save-tasks-dialog {
  top: 0 !important;
  align-self: flex-start;
  margin-top: 24px;
  max-height: min(calc(100vh - 48px), 640px);
}

.gantt-chip {
  display:flex;
  align-items:center;
  justify-content:center;
  border-radius:1em;
  margin-inline-start:0.5em;
  padding:0.3em 1em;
  color:#fff;
}

.gantt-chip--subtasks {
  background-color:#3ca3f4;
  cursor:pointer;
}

.gantt-chip--subtasks-changed {
  background-color:#ff9800;
}

.v-chip {
  padding: 0 4px !important;
}

/* Vuetify clips .v-alert__content (overflow: hidden); a switch thumb in the
   off position overhangs its track on the left and lost half its width. */
.editable-ranges-alert .v-alert__content {
  overflow: visible;
}
</style>
